"use client"

import { cva, type VariantProps } from "class-variance-authority"
import { motion, useReducedMotion } from "motion/react"
import { forwardRef, type HTMLAttributes } from "react"
import { EASE_LINEAR } from "../../lib/ease"
import { cn } from "../../lib/utils"

/** Sweep duration and the pause before it runs again, in seconds. */
const SHIMMER_DURATION = 1.5
const SHIMMER_REPEAT_DELAY = 0.25

/** Width of the final line in a text block, so it reads as a paragraph end. */
const LAST_LINE_WIDTH = "62%"

const skeletonVariants = cva("relative isolate overflow-hidden bg-v2-muted", {
  variants: {
    variant: {
      /** One row of text. Height matches the v2 body line box. */
      line: "h-3 w-full rounded-v2-sm",
      /** Avatar or icon placeholder. */
      circle: "size-9 rounded-full",
      /** Block placeholder for a card, thumbnail, or chart well. */
      rect: "h-24 w-full rounded-v2-lg",
    },
  },
  defaultVariants: { variant: "line" },
})

export interface SkeletonProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  /** Runs the shimmer sweep. Set false for a still block. */
  shimmer?: boolean
}

/**
 * Placeholder block for content that has not arrived.
 *
 * The sweep is a gradient band translated across the block rather than an
 * opacity pulse, because a pulse on a muted fill reads as a disabled control
 * while a sweep reads as work in progress.
 *
 * The band darkens in light and lightens in dark, which is the opposite of the
 * usual instinct. A lightening band is the conventional shimmer, but the light
 * track is `--v2-muted` at 95.9% lightness and the page white above it is 100%:
 * even at full alpha that is a four-value channel delta, so the sweep simply is
 * not there. Going the other way gives roughly a ten percent delta in both
 * themes, which is the smallest step that actually reads at a 12px line height.
 *
 * Marked `aria-hidden`, because the shape carries no information. Announce the
 * wait on the region that owns it — the `label` on {@link SkeletonText}, or
 * `aria-busy` on your own container — so it is stated once rather than once per
 * block.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, variant, shimmer = true, ...props },
  ref,
) {
  const reduce = useReducedMotion()

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-slot="skeleton"
      className={cn(skeletonVariants({ variant }), className)}
      {...props}
    >
      {shimmer && !reduce ? (
        <motion.div
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-v2-muted-foreground/20 to-transparent dark:via-v2-foreground/10"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{
            duration: SHIMMER_DURATION,
            repeat: Infinity,
            repeatDelay: SHIMMER_REPEAT_DELAY,
            ease: EASE_LINEAR,
          }}
        />
      ) : null}
    </div>
  )
})

Skeleton.displayName = "Skeleton"

export interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  /** Rows to render. The last one is shortened. */
  lines?: number
  /** Width of the last row. Pass any CSS length or percentage. */
  lastLineWidth?: string
  /** Runs the shimmer sweep on every row. */
  shimmer?: boolean
  /** Announced while the block is up. */
  label?: string
}

/**
 * A stack of {@link Skeleton} lines standing in for a paragraph.
 *
 * This is the piece that owns the announcement: it is a live `status` region
 * carrying one screen-reader-only label for the whole block, so a six-line
 * placeholder does not read out six times.
 *
 * Deliberately no `aria-busy` here. A live region that declares itself busy
 * permits assistive technology to defer exposing it, and since this region only
 * ever unmounts, the flag would never clear and the one label it exists to speak
 * could go unspoken. `aria-busy` belongs on the container that will hold the
 * loaded content, which is the element that stops being busy.
 *
 * The row pitch is the `text-v2-base` line box: a 12px bar plus a 6px gap, so a
 * paragraph placeholder occupies the height of the paragraph it replaces rather
 * than running taller.
 */
export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(
  function SkeletonText(
    {
      className,
      lines = 3,
      lastLineWidth = LAST_LINE_WIDTH,
      shimmer = true,
      label = "Loading",
      ...props
    },
    ref,
  ) {
    const rows = Math.max(lines, 1)

    return (
      <div
        ref={ref}
        role="status"
        data-slot="skeleton-text"
        className={cn("flex w-full flex-col gap-1.5", className)}
        {...props}
      >
        <span className="sr-only">{label}</span>
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton
            key={index}
            variant="line"
            shimmer={shimmer}
            style={index === rows - 1 ? { width: lastLineWidth } : undefined}
          />
        ))}
      </div>
    )
  },
)

SkeletonText.displayName = "SkeletonText"
